import { useEffect, useState } from 'react';
import { isWeb, localTime } from '@helpers';
import { Moment } from 'moment';
import { ChartData, ChartDataItem, GetDateParams, SwipeConfig, UseAnimatedChartParams } from '@components/machines/components/eventsChart/types';
import { getPeriodStep } from '@components/machines/components/eventsChart/helpers/period';
import { DATE_FORMAT } from '@v2/pages/common/constants/base.constants';

interface UseAnimatedChartReturn {
  renderChart: ChartDataItem[];
  renderChartPrev: ChartDataItem[];
  selectedFlatListIndex: number | null;
  saveChartData: (date: string, data: ChartData) => void;
  saveChartDataPrev: (date: string, dataPrev: ChartData) => void;
  userStopScroll: (contentOffsetX: number) => void;
  config: SwipeConfig;
}

export const useAnimatedChart = ({
  selectedTime,
  selectedChartDate,
  id,
  flatListRef,
  flatListItemWidth,
  showLine,
  resetMobileChartCount,
  setSelectedChartDate,
}: UseAnimatedChartParams): UseAnimatedChartReturn => {
  const config: SwipeConfig = {
    velocityThreshold: 0.3,
    directionalOffsetThreshold: 80,
  };

  // Helper function to get date for FlatList items
  const getDate = ({ initial, action, amount, start }: GetDateParams): ChartDataItem | null => {
    let date: Moment = localTime(initial);
    const { unit, count } = getPeriodStep(selectedTime);
    const stepAmount = (amount || 1) * count;
    if (action) {
      switch (action) {
        case 'subtract': {
          date.subtract(stepAmount, unit);
          break;
        }
        case 'add': {
          date.add(stepAmount, unit);
          break;
        }
      }
    }

    const formattedDate: string = date.format(DATE_FORMAT);

    if (localTime(formattedDate).endOf(unit).isAfter(localTime().endOf(unit))) {
      return null;
    }

    return {
      date: formattedDate,
      start,
      // Each item will load its own data when BarChart renders
      data: null,
    };
  };

  // Initialize chart data for FlatList
  const initializeChartData = (): void => {
    // Format the date properly - if it's a timestamp, convert to YYYY-MM-DD
    const selectedDate: string = selectedChartDate
      ? localTime(selectedChartDate).format(DATE_FORMAT)
      : localTime().format(DATE_FORMAT);

    const newData: ChartDataItem[] = [
      getDate({ initial: selectedDate, action: 'add', amount: 2 }),
      getDate({ initial: selectedDate, action: 'add', amount: 1 }),
      getDate({ initial: selectedDate, start: true }),
      getDate({ initial: selectedDate, action: 'subtract', amount: 1 }),
    ].filter((i): i is ChartDataItem => i !== null);

    setRenderChart(newData);
    // Initialize renderChartPrev with same structure
    setRenderChartPrev(newData.map((item) => ({ ...item, data: null })));

    const newIndex: number = newData.findIndex((i) => i.start);
    setSelectedFlatListIndex(newIndex);

    // Only scroll if FlatList is already mounted and this is not the initial render
    if (!isWeb && flatListRef.current && selectedFlatListIndex !== null) {
      setTimeout(() => {
        flatListRef.current?.scrollToIndex({
          index: newIndex,
          animated: false,
        });
      }, 0);
    }
  };

  // Initialize with empty array first
  const [renderChart, setRenderChart] = useState<ChartDataItem[]>([]);
  const [renderChartPrev, setRenderChartPrev] = useState<ChartDataItem[]>([]);
  const [selectedFlatListIndex, setSelectedFlatListIndex] = useState<number | null>(null);

  // Save chart data after it's loaded from API
  const saveChartData = (date: string, data: ChartData): void => {
    setRenderChart((currentValues) => currentValues.map((i) => (i.date === date ? { ...i, data } : i)));
  };

  // Save previous chart data after it's loaded from API
  const saveChartDataPrev = (date: string, dataPrev: ChartData): void => {
    setRenderChartPrev((currentValues) => {
      // If array doesn't exist yet, initialize it with same structure as renderChart
      if (currentValues.length === 0 && renderChart.length > 0) {
        return renderChart.map((i) => (i.date === date ? { ...i, data: dataPrev } : { ...i, data: null }));
      }
      return currentValues.map((i) => (i.date === date ? { ...i, data: dataPrev } : i));
    });
  };

  // Reset data when important props change
  useEffect(() => {
    if (!isWeb) {
      initializeChartData();
    }
  }, [id, selectedTime, resetMobileChartCount, showLine]);

  // Load past data for infinite scroll
  const loadPastData = (): void => {
    if (renderChart.length === 0) return;
    const date: ChartDataItem | null = getDate({ initial: renderChart[renderChart.length - 1].date, action: 'subtract', amount: 1 });
    if (date) {
      setRenderChart([...renderChart, date]);
      setRenderChartPrev([...renderChartPrev, { ...date, data: null }]);
    }
  };

  // Load future data for infinite scroll
  const loadFutureData = (): void => {
    if (renderChart.length === 0) return;
    const date: ChartDataItem | null = getDate({ initial: renderChart[0].date, action: 'add', amount: 1 });
    if (date) {
      setRenderChart([date, ...renderChart]);
      setRenderChartPrev([{ ...date, data: null }, ...renderChartPrev]);
    }
  };

  // Handle scroll stop
  const userStopScroll = (contentOffsetX: number): void => {
    const i: number = Math.round(contentOffsetX / flatListItemWidth);
    const date: string | undefined = renderChart[i]?.date;
    const { unit } = getPeriodStep(selectedTime);
    const isEnd: boolean = !!date && localTime(date).endOf(unit).isSameOrAfter(localTime().endOf(unit));

    setSelectedFlatListIndex(isEnd ? i : i || 1);
    if (date && setSelectedChartDate) {
      setSelectedChartDate(date);
    }
    if (i === 0) {
      loadFutureData();
    }
    if (i >= renderChart.length - 2) {
      loadPastData();
    }
  };

  return {
    renderChart,
    renderChartPrev,
    selectedFlatListIndex,
    saveChartData,
    saveChartDataPrev,
    userStopScroll,
    config,
  };
};
